import React from 'react'

export type DirectChallengePartySize = 1 | 2

/** Warm gold/tan for party-format person icons (matches selected card accent). */
const PARTY_FORMAT_ICON_COLOR = '#c9a96e'

const FORMATS: { size: DirectChallengePartySize }[] = [{ size: 1 }, { size: 2 }]

function PartyPersonCanvas({
  style,
}: {
  style: React.CSSProperties
}) {
  const canvasRef = React.useRef<HTMLCanvasElement | null>(null)

  React.useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const size = 96
    canvas.width = size
    canvas.height = size
    ctx.clearRect(0, 0, size, size)
    ctx.fillStyle = PARTY_FORMAT_ICON_COLOR
    ctx.shadowColor = 'rgba(0, 0, 0, 0.55)'
    ctx.shadowBlur = 5
    ctx.shadowOffsetY = 2

    ctx.beginPath()
    ctx.arc(48, 28, 18, 0, Math.PI * 2)
    ctx.fill()

    ctx.beginPath()
    ctx.moveTo(19, 83)
    ctx.bezierCurveTo(20, 59, 31, 48, 48, 48)
    ctx.bezierCurveTo(65, 48, 76, 59, 77, 83)
    ctx.closePath()
    ctx.fill()
  }, [])

  return <canvas ref={canvasRef} aria-hidden style={style} />
}

/** Party-size silhouettes; `iconCqh` scales icon edge length as % of query container height. */
export function PartyFormatIcons({
  count,
  iconCqh = 14,
}: {
  count: DirectChallengePartySize
  iconCqh?: number
}) {
  const iconDims = { width: `${iconCqh}cqh`, height: `${iconCqh}cqh` } as const
  /** ~20% overlap between adjacent icons */
  const overlapX = `calc(-0.2 * ${iconCqh}cqh)`
  const overlapY = `calc(-0.2 * ${iconCqh}cqh)`
  const common = {
    ...iconDims,
    color: PARTY_FORMAT_ICON_COLOR,
    flexShrink: 0,
  } as const

  if (count === 1) {
    return <PartyPersonCanvas style={common} />
  }

  if (count === 2) {
    return (
      <div className="flex flex-row items-center justify-center">
        <PartyPersonCanvas style={common} />
        <PartyPersonCanvas style={{ ...common, marginLeft: overlapX }} />
      </div>
    )
  }

  return (
    <div className="flex flex-col items-center justify-center">
      <PartyPersonCanvas style={common} />
      <div
        className="flex flex-row items-center justify-center"
        style={{ marginTop: overlapY }}
      >
        <PartyPersonCanvas style={common} />
        <PartyPersonCanvas style={{ ...common, marginLeft: overlapX }} />
      </div>
    </div>
  )
}

export interface DirectChallengeCharacterCountPanelProps {
  partySize: DirectChallengePartySize
  onPartySizeChange: (size: DirectChallengePartySize) => void
  disabled?: boolean
}

export const DirectChallengeCharacterCountPanel: React.FC<DirectChallengeCharacterCountPanelProps> = ({
  partySize,
  onPartySizeChange,
  disabled = false,
}) => {
  return (
    <div
      className={
        'flex min-h-0 flex-1 flex-col' + (disabled ? ' pointer-events-none opacity-50' : '')
      }
      style={{
        containerType: 'size',
        minHeight: 0,
      }}
    >
      <div className="flex min-h-0 flex-1 flex-row" style={{ minHeight: 0, gap: '2%' }}>
        {FORMATS.map(({ size }) => {
          const selected = partySize === size
          return (
            <button
              key={size}
              type="button"
              aria-pressed={selected}
              onClick={() => !disabled && onPartySizeChange(size)}
              style={{ containerType: 'size', minHeight: 0 }}
              className={`box-border flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-md border p-[3%] transition ${
                selected
                  ? 'border-amber-400/70 bg-amber-950/35 shadow-[inset_0_1px_0_rgba(255,255,255,0.08)] ring-1 ring-amber-400/35'
                  : 'border-white/12 bg-black/25 hover:border-amber-400/35 hover:bg-black/40'
              }`}
            >
              <div
                className="flex min-h-0 w-full flex-col items-center justify-center"
                style={{ flex: '0 0 70%', minHeight: 0 }}
              >
                <PartyFormatIcons count={size} />
              </div>
              <div
                className="flex min-h-0 w-full flex-col items-center justify-center"
                style={{ flex: '0 0 32%', minHeight: 0 }}
              >
                <div
                  className="flex flex-row flex-nowrap items-center justify-center text-amber-200"
                  style={{
                    gap: '3.5cqw',
                    fontFamily: "'Tilt Warp', sans-serif",
                    textShadow:
                      '0 1px 2px rgba(0, 0, 0, 0.9), 0 0 12px rgba(251, 191, 36, 0.35)',
                  }}
                >
                  <span style={{ fontSize: '14cqh', lineHeight: 1, display: 'inline-block' }}>{size}</span>
                  <span style={{ fontSize: '8.5cqh', lineHeight: 1, display: 'inline-block' }}>vs</span>
                  <span style={{ fontSize: '14cqh', lineHeight: 1, display: 'inline-block' }}>{size}</span>
                </div>
              </div>
            </button>
          )
        })}
      </div>
    </div>
  )
}
